<script lang="ts" setup>
import orderBy from 'lodash/orderBy';
import { computed } from 'vue';
import { applyPhoneMask } from '@/utils/number';
import { formatSecToTime } from '@/utils/datetime';
import { RealtimeReportServiceGetQueueCallsStateResult } from '@/types';

const props = defineProps<{
    queueCalls: RealtimeReportServiceGetQueueCallsStateResult[];
}>();

const computedQueueCalls = computed(() => {
    const sorted = orderBy(
        props.queueCalls,
        [
            (qc) => qc.queueModel.name || '',
            (qc) => qc.callerIdNum || '',
            (qc) => qc.destinationNumber || '',
        ],
        ['asc', 'asc', 'asc'],
    );

    return sorted.map((qc) => ({
        key: qc.uniqueId,
        clientNumber: applyPhoneMask(qc.callerIdNum || ''),
        destinationNumber: applyPhoneMask(qc.destinationNumber || ''),
        queueName: qc.queueModel.name || '',
        wait: formatSecToTime(qc.wait || 0),
    }));
});
</script>

<template>
    <div class="relative flex flex-col">
        <h1 class="mb-2 text-lg font-semibold">
            {{ $t('report.realtime.queueCallsTable.title') }}
        </h1>
        <UiPanel class="flex-1 overflow-y-auto" no-padding>
            <table class="min-w-full border-separate border-spacing-0">
                <thead>
                    <tr>
                        <th
                            v-for="header in [
                                'clientNumber',
                                'destinationNumber',
                                'queueName',
                                'wait',
                            ]"
                            :key="header"
                            class="sticky top-0 z-10 whitespace-nowrap border-b border-gray-300 bg-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 backdrop-blur backdrop-filter"
                            :class="{
                                'text-left': [
                                    'clientNumber',
                                    'destinationNumber',
                                    'queueName',
                                ].includes(header),
                                'text-center': ['wait'].includes(header),
                            }"
                        >
                            {{ $t(`report.realtime.queueCallsTable.header.${header}`) }}
                        </th>
                    </tr>
                </thead>
                <tbody v-if="computedQueueCalls.length">
                    <tr v-for="item in computedQueueCalls" :key="item.key" class="even:bg-gray-100">
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.clientNumber }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.destinationNumber }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-sm font-medium"
                        >
                            {{ item.queueName }}
                        </td>
                        <td
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-1.5 text-center text-sm font-medium"
                        >
                            {{ item.wait }}
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr class="text-gray-700">
                        <td
                            colspan="4"
                            class="whitespace-nowrap border-b border-gray-200 px-3 py-4 text-center text-sm font-medium"
                        >
                            {{ $t('report.realtime.queueCallsTable.empty') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </UiPanel>
    </div>
</template>
